import { useStorage } from "@vueuse/core"

interface UseLocalStorageOptions {
  needLogin?: boolean
}

type LocalStorageKeys =
  | "oneClickBet"
  | "pinned-EXCHANGE"
  | "pinned-SPORTSBOOK"
  | "pinned-FANCYBET"
  | "pinned-BOOKMAKER"
  | "pinned-GENIUSSPORTS"
  | "enableSpark"
  | "fancyBetAndBookMakerAcceptAnyOddsLocalStorage"
  | "sportsBookAcceptAnyOdds"
  | "geniusSportsAcceptAnyOdds"
  | "confirmBets"
  | "certification"

export const useLocalStorage = <T>(
  key: LocalStorageKeys,
  defaultValue: T,
  options: UseLocalStorageOptions = {}
) => {
  const { needLogin = false } = options

  const authStore = useAuthStore()
  const isLogin = computed(() => authStore.isLogin)
  const userID = computed(() => authStore.userID)

  const localStorage = ref(defaultValue)
  const tempStorage = ref()

  const createStorageInstance = () => {
    const storageKey = needLogin ? `${key}-${userID.value}` : key

    tempStorage.value = useStorage<T>(storageKey, defaultValue, window.localStorage, {
      serializer: {
        read: JSON.parse,
        write: JSON.stringify
      }
    })
  }

  const reset = () => {
    localStorage.value = tempStorage.value.value ?? defaultValue
  }

  const update = (value?: T) => {
    const newValue = value ?? defaultValue

    tempStorage.value.value = newValue
  }

  watch(
    () => tempStorage.value,
    () => {
      localStorage.value = tempStorage.value.value
    },
    {
      deep: true
    }
  )

  watch(
    () => isLogin.value,
    isLoggedIn => {
      if ((isLoggedIn && needLogin) || !needLogin) {
        createStorageInstance()
      }
      if (!isLoggedIn && needLogin) {
        localStorage.value = ref(defaultValue).value
      }
    },
    { immediate: true }
  )

  return {
    localStorage,
    update,
    reset
  }
}

export default useLocalStorage
